import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Chip, Box } from '@mui/material';
import { MdAdd, MdClose } from 'react-icons/md';
import AppLayout from '../components/AppLayout';
import { addTemplates, addTemplatesByBusiness } from '../logic/customTemplates/action';

const AddTemplates = () => {
  const [name, setname] = useState('');
  const [message, setmessage] = useState('');
  const [variables, setvariables] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectorAuth = useSelector(state => state.auth);

  const generateVaribles = () => {
    const dt = message.match(/{([^}]*)}/g);
    setvariables(
      dt.map(e => ({
        variable: e,
        relate: ['{patient_name}', '{appointment_date}', '{appointment_time}', '{appointment_duration}', '{doctor_name}', '{business_name}'].includes(e) ? e.replace('{', '').replace('}', '').replace('_', ' ') : '',
      }))
    );
  };

  const editVarible = (target, value) => {
    let newArr = [...variables];
    newArr[target].relate = value;
    setvariables(newArr);
  };

  return (
    <AppLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className='flex-1 h-full overflow-y-auto pt-6 flex flex-col items-center gap-8 p-6 md:p-10'
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="w-full max-w-4xl"
        >
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-8 md:p-10">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className='text-3xl md:text-4xl font-bold text-center mb-2 bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600 bg-clip-text text-transparent'
            >
              Add New Template
            </motion.h1>
            <p className="text-center text-gray-600 mb-8">Create a message template with dynamic variables</p>
            
            <div className='w-full flex flex-col gap-6'>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <TextField
                  fullWidth
                  label="Template Name"
                  placeholder='Template Name'
                  value={name}
                  onChange={e => setname(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Template Message"
                  placeholder='Template Message'
                  value={message}
                  onChange={e => setmessage(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Button
                  onClick={generateVaribles}
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    py: 1.5,
                    px: 4,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                    },
                  }}
                >
                  Generate Variables
                </Button>
              </motion.div>
              {variables.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className='flex items-start flex-col gap-4 w-full bg-gray-50/50 rounded-xl p-6'
                >
                  <h4 className='font-bold text-lg text-gray-800 mb-2'>Variable Mapping</h4>
                  {variables.map((ele, i) => (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + i * 0.1 }}
                      className='flex gap-4 w-full items-center' key={i}
                    >
                      <TextField
                        className='flex-1'
                        type='text'
                        disabled
                        value={ele.variable}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          },
                        }}
                      />
                  {['{patient_name}', '{appointment_date}', '{appointment_time}', '{appointment_duration}', '{doctor_name}', '{business_name}'].includes(ele.variable) ? (
                        <Chip
                          label={ele.relate}
                          sx={{
                            backgroundColor: '#667eea',
                            color: 'white',
                            fontWeight: 600,
                            minWidth: '150px',
                          }}
                        />
                      ) : (
                        <TextField
                          select
                          className='flex-1'
                          onChange={ev => editVarible(i, ev.target.value)}
                          value={ele.relate}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            },
                          }}
                        >
                      <option value=''>Select Field</option>
                      <option value='Patient Name'>Patient Name</option>
                      <option value="Patient Phone">Patient Phone</option>
                      <option value="Staff Name">Staff Name</option>
                      <option value="Staff Phone">Staff Phone</option>
                      <option value="Start Date">Start Date</option>
                      <option value="End Date">End Date</option>
                      <option value="Start Time">Start Time</option>
                      <option value="End Time">End Time</option>
                      <option value="Service Name">Service Name</option>
                      <option value="Business Name">Business Name</option>
                      <option value="Business Phone">Business Phone</option>
                      <option value="Business Address">Business Address</option>
                        </TextField>
                      )}
                    </motion.div>
                  ))}
                </motion.div>
              )}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className='w-full'
              >
                <h4 className='font-bold text-lg text-gray-800 mb-4 text-center'>Predefined Dynamic Variables</h4>
                <div className='text-center w-full flex flex-wrap gap-3 justify-center'>
                  {[
                    { label: 'Patient Name', var: '{patient_name}' },
                    { label: 'Patient Phone', var: '{patient_phone}' },
                    { label: 'Appointment Date', var: '{appointment_date}' },
                    { label: 'Start Time', var: '{start_time}' },
                    { label: 'Start Date', var: '{start_date}' },
                    { label: 'End Time', var: '{end_time}' },
                    { label: 'End Date', var: '{end_date}' },
                    { label: 'Business Name', var: '{business_name}' },
                    { label: 'Business Phone', var: '{business_phone}' },
                    { label: 'Business Address', var: '{business_address}' },
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Chip
                        label={item.label}
            onClick={() => {
                          setmessage(prevMessage => prevMessage + item.var);
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          fontWeight: 600,
                          cursor: 'pointer',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                          },
                        }}
                      />
                    </motion.div>
                  ))}
          </div>
                <p className='text-gray-600 text-center w-full mt-4 text-sm'>
                  Click on variables above to add them to your message template
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="w-full mt-4"
              >
                <Button
            onClick={() => {
              if (selectorAuth.user.role !== 'super_admin') {
                addTemplatesByBusiness(dispatch, {
                  template_name: name,
                  template: message,
                  varibles: variables,
                }, navigate);
              } else {
                addTemplates(dispatch, {
                  name,
                  message,
                  varibles: variables,
                }, navigate);
              }
            }}
                  fullWidth
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    py: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                    },
                  }}
                >
                  Add Template
                </Button>
              </motion.div>
        </div>
      </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
};

export default AddTemplates;
